import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TierContext = createContext(null);

export const TierProvider = ({ children }) => {
  const { api, user, loading: authLoading } = useAuth();
  const [tierData, setTierData] = useState(null);
  const [aiStatus, setAiStatus] = useState({ ai_enabled: false, has_api_key: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchTierData = useCallback(async () => {
    if (!user || !api) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(false);
    try {
      const [tierResponse, aiResponse] = await Promise.all([
        api.get('/billing/usage'),
        api.get('/ai/status').catch(() => ({ data: { ai_enabled: false, has_api_key: false } }))
      ]);
      setTierData(tierResponse.data);
      setAiStatus(aiResponse.data);
    } catch (err) {
      console.error('Failed to fetch tier data:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (user) {
      fetchTierData();
    } else {
      setTierData(null);
      setLoading(false);
    }
  }, [user, authLoading, fetchTierData]);

  const hasFeature = useCallback((feature) => {
    // While loading or if there's an error, block restricted features by default
    if (loading || !tierData) return false;
    
    // For AI features, also check if AI is enabled in tenant settings
    const aiFeatures = ['ai_features', 'smart_suggestions', 'ocr_dockets'];
    if (aiFeatures.includes(feature)) {
      // Must have feature in tier AND ai_enabled must be true
      return tierData.features?.includes(feature) && aiStatus.ai_enabled;
    }
    
    return tierData.features?.includes(feature) ?? false;
  }, [tierData, aiStatus, loading]);

  // Check if tier includes a feature (without checking if enabled)
  // Use this for Settings pages where user can toggle features on/off
  const hasTierFeature = useCallback((feature) => {
    if (loading || !tierData) return false;
    return tierData.features?.includes(feature) ?? false;
  }, [tierData, loading]);

  // Specific check for AI being fully available (tier + enabled)
  const isAiAvailable = useCallback(() => {
    if (loading || !tierData) return false;
    const hasAiTier = tierData.features?.includes('ai_features');
    return hasAiTier && aiStatus.ai_enabled;
  }, [tierData, aiStatus, loading]);

  const checkLimit = useCallback((resourceType) => {
    // If still loading or no data, be conservative and disallow
    if (loading || !tierData) return { allowed: false, loading: true };
    
    if (resourceType === 'operator') {
      const used = tierData.operators_used || 0;
      const limit = tierData.operators_total_limit || 0;
      return {
        allowed: used < limit,
        used,
        limit,
        remaining: limit - used
      };
    }
    
    if (resourceType === 'user') {
      const used = tierData.users_used || 0;
      const limit = tierData.users_total_limit || 0;
      return {
        allowed: used < limit,
        used,
        limit,
        remaining: limit - used
      };
    }
    
    return { allowed: true };
  }, [tierData]);

  const getRequiredTier = useCallback((feature) => {
    // Feature to minimum required tier mapping
    const featureTiers = {
      'equipment': 'basic',
      'operators': 'basic',
      'jobs': 'basic',
      'schedule': 'basic',
      'customers': 'basic',
      'sites': 'basic',
      'dockets': 'basic',
      'subcontractors': 'advanced',
      'worker_types': 'basic',
      'basic_forms': 'basic',
      'custom_forms': 'advanced',
      'standard_reports': 'advanced',
      'premium_reports': 'professional',
      'compliance': 'advanced',
      'timesheets': 'advanced',
      'messaging': 'advanced',
      'quotes': 'advanced',
      'quote_templates': 'professional',
      'quote_analytics': 'professional',
      'data_import': 'advanced',
      'ai_features': 'professional',
      'smart_suggestions': 'professional',
      'ocr_dockets': 'professional',
      'api_access': 'professional'
    };
    return featureTiers[feature] || 'basic';
  }, []);

  const value = {
    tierData,
    loading,
    hasFeature,
    hasTierFeature,
    checkLimit,
    getRequiredTier,
    refreshTierData: fetchTierData,
    currentTier: tierData?.tier || 'basic',
    tierName: tierData?.tier_name || 'Basic',
    aiStatus,
    isAiAvailable
  };

  return (
    <TierContext.Provider value={value}>
      {children}
    </TierContext.Provider>
  );
};

export const useTier = () => {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
};

export default TierContext;
